
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'src/redux/store';
import useAuth from 'src/hooks/useAuth';
import useResponsive from 'src/hooks/useResponsive';

// actions
import search from 'src/actions/chats/staff/search';
import {
  setSearchResults,
  getChatInternoChats
} from 'src/redux/slices/staff';

// components
import Iconify from 'src/components/Iconify';
import { Input } from "src/@/components/ui/input";
import { Button } from 'src/@/components/ui/button';
import NewChat from './components/NewChat';
import ChatInternoListaChats from './ChatInternoListaChats';

export default function ChatInternoSidebar({
  contactList,
  onCallback
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useResponsive('down', 'md');
  const [openSidebar, setOpenSidebar] = useState(true);
  const [showSearch, setShowSearch] = useState(!contactList);

  const [searchQuery, setSearchQuery] = useState('');

  const { staffs, roomsWithNewMessages } = useSelector((state) => state.staff);
  const { user } = useAuth();
  const clienteId = user.clienteId;

  // Search initial data
  useEffect(() => {
    search().then(res => dispatch(setSearchResults(res.data.users))).catch(err => console.log(err));
  }, [dispatch]);

  // Load chats
  useEffect(() => {
    if (user?.id && clienteId) {
      dispatch(getChatInternoChats(clienteId, user.id));
    }
  }, [clienteId, user?.id, dispatch]);

  // Handle Mobile
  useEffect(() => {
    if (isMobile) {
      setOpenSidebar(false);
    } else {
      setOpenSidebar(true);
    }
  }, [isMobile]);

  // Filter chats
  const chats = useMemo(() => {
    if (!staffs) return [];
    return staffs.filter((contact) => {
      if (!searchQuery) return true;

      const searchLower = searchQuery.toLocaleLowerCase();
      if (contact?.title?.toLocaleLowerCase().includes(searchLower)) {
        return true;
      }

      const peopleFiltered = contact.people || [];
      if (!contact.isGroup && peopleFiltered.some(person => {
        const nomeFantasia = person?.nomeFantasia?.toLocaleLowerCase() || '';
        const firstName = person?.firstName?.toLocaleLowerCase() || '';
        const lastName = person?.lastName?.toLocaleLowerCase() || '';

        return nomeFantasia.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower);
      })) {
        return true;
      }

      return false;
    });
  }, [staffs, searchQuery]);

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-neutral-900 border-r border-gray-100 dark:border-neutral-800 overflow-hidden ${contactList ? 'w-full' : 'w-[400px] max-w-[400px]'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-[52px] border-b border-gray-100 dark:border-neutral-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
            <Iconify icon="mdi:chat-processing" width={14} className="text-violet-600 dark:text-violet-400" />
          </div>
          <span className="text-[14px] font-bold text-gray-900 dark:text-gray-100">
            {contactList ? t('chat.internal.contacts') : t('chat.internal.title')}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!contactList && (
            <Button
              onClick={() => setShowSearch(!showSearch)}
              size="icon"
              variant="ghost"
              className={`w-7 h-7 rounded-lg ${
                showSearch
                  ? 'bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              <Iconify icon="eva:search-outline" width={16} />
            </Button>
          )}
          <NewChat
            icon
            listaChats={chats}
            onCallback={() => {
              dispatch(getChatInternoChats(clienteId, user.id))
            }}
          />
        </div>
      </div>

      {/* Search */}
      {showSearch && !contactList && (
        <div className="px-3 pt-3 pb-1 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="relative">
            <Input
              placeholder={contactList ? t('chat.internal.searchContactPlaceholder') : t('chat.internal.searchConversationPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8 bg-gray-50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 rounded-lg text-sm focus-visible:ring-0 focus-visible:border-gray-300 shadow-none"
            />
            <Iconify
              icon="eva:search-outline"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 w-4 h-4"
            />
          </div>
        </div>
      )}

      {/* Chat count */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-neutral-800 flex-shrink-0">
        <span className="text-[11px] font-medium text-gray-400 dark:text-neutral-500 uppercase tracking-wider">
          {chats.length} {chats.length === 1 ? t('chat.internal.conversationSingular') : t('chat.internal.conversationPlural')}
        </span>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <ChatInternoListaChats
          contactList={contactList}
          chats={chats}
          isOpenSidebar={openSidebar}
          user={user}
          roomsWithNewMessages={roomsWithNewMessages}
          onCallback={onCallback}
        />
      </div>
    </div>
  );
}
